import { useEffect, useState } from 'react'
import { Mail, RefreshCcw, ShieldCheck } from 'lucide-react'
import { authorizeGoogle } from '../lib/drive'
import { estimateGmailBytes, GMAIL_SCOPE, listGmailMessages } from '../lib/gmail'
import { formatBytes } from '../lib/rules'

export type GmailPreflightSummary = {
  status: 'idle' | 'connecting' | 'analyzing' | 'ready' | 'error'
  count: number
  estimatedBytes: number
  sampledCount: number
  message: string
}

const initialSummary: GmailPreflightSummary = {
  status: 'idle',
  count: 0,
  estimatedBytes: 0,
  sampledCount: 0,
  message: ''
}

interface GmailPreflightProps {
  clientId: string
  clientReady: boolean
  resetKey: number
  query: string
  selectionLabel: string
  onChange: (summary: GmailPreflightSummary) => void
}

export function GmailPreflight({ clientId, clientReady, resetKey, query, selectionLabel, onChange }: GmailPreflightProps) {
  const [summary, setSummary] = useState<GmailPreflightSummary>(initialSummary)

  const updateSummary = (next: GmailPreflightSummary) => {
    setSummary(next)
    onChange(next)
  }

  useEffect(() => {
    updateSummary(initialSummary)
    // El reinicio es intencional cuando cambian fuentes, objetivo o fechas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  const connectAndAnalyze = async () => {
    if (!clientReady || summary.status === 'connecting' || summary.status === 'analyzing') return

    try {
      updateSummary({ ...initialSummary, status: 'connecting', message: 'Abriendo el consentimiento oficial de Google para Gmail…' })
      const token = await authorizeGoogle(clientId, GMAIL_SCOPE, 'Gmail')
      updateSummary({ ...initialSummary, status: 'analyzing', message: 'Leyendo los correos que coinciden con tu selección. No descargamos ni modificamos mensajes todavía.' })
      const messages = await listGmailMessages(token, query, (count) => {
        updateSummary({ ...initialSummary, status: 'analyzing', count, message: `Contando Gmail: ${count.toLocaleString('es-ES')} correos encontrados…` })
      })

      if (!messages.length) {
        updateSummary({ ...initialSummary, status: 'ready', message: 'No hay correos que coincidan con esta selección.' })
        return
      }

      updateSummary({ ...initialSummary, status: 'analyzing', count: messages.length, message: `Calculando el tamaño estimado a partir de una muestra de tus ${messages.length.toLocaleString('es-ES')} correos…` })
      const size = await estimateGmailBytes(token, messages, (completed, total) => {
        updateSummary({ ...initialSummary, status: 'analyzing', count: messages.length, message: `Estimando tamaño de Gmail: ${completed.toLocaleString('es-ES')} de ${total.toLocaleString('es-ES')} correos de muestra…` })
      })

      updateSummary({
        status: 'ready',
        count: messages.length,
        estimatedBytes: size.estimatedBytes,
        sampledCount: size.sampledCount,
        message: `Gmail analizado: ${messages.length.toLocaleString('es-ES')} correos · tamaño estimado ${formatBytes(size.estimatedBytes)}. Los adjuntos se conservarán dentro de cada archivo .eml.`
      })
    } catch (error) {
      updateSummary({
        ...initialSummary,
        status: 'error',
        message: error instanceof Error ? error.message : 'No se pudo analizar Gmail.'
      })
    }
  }

  const loading = summary.status === 'connecting' || summary.status === 'analyzing'

  return <section className={`gmail-preflight ${summary.status}`} aria-live="polite">
    <div className="gmail-preflight-head">
      <div>
        <span className="kicker">GMAIL · SOLO LECTURA</span>
        <h2>Analiza tus correos antes de guardar.</h2>
        <p>Google mostrará el permiso de lectura. Contamos tus correos y estimamos cuánto ocuparán, sin descargar ni modificar ningún mensaje durante este paso.</p>
      </div>
      <button className="button button-primary" type="button" onClick={connectAndAnalyze} disabled={!clientReady || loading || summary.status === 'ready'}>
        <Mail size={18} />
        {loading ? <RefreshCcw className="spin" size={18} /> : null}
        {summary.status === 'ready' ? 'Gmail analizado' : loading ? 'Analizando…' : 'Conectar y analizar Gmail'}
      </button>
    </div>

    {!clientReady && <div className="analysis-activation"><ShieldCheck size={19} /><div><strong>Conexión directa preparada</strong><p>Activa el Client ID de producción para que Google pueda mostrar el consentimiento de Gmail.</p></div></div>}

    {summary.message && <p className={summary.status === 'error' ? 'analysis-message is-error' : 'analysis-message'}>{summary.message}</p>}

    {summary.status !== 'ready' && <div className="analysis-expectations"><div><span>01</span><strong>Correos incluidos</strong><small>Verás cuántos mensajes coinciden con {selectionLabel.toLowerCase()}.</small></div><div><span>02</span><strong>Tamaño estimado</strong><small>Usaremos `sizeEstimate` de Gmail sin descargar los mensajes completos.</small></div><div><span>03</span><strong>Adjuntos preservados</strong><small>Al copiar, viajan dentro de cada archivo .eml.</small></div></div>}
  </section>
}
